import React from 'react';
import { Box, Typography, Stack, Paper, Chip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faPhone,
  faFileLines,
  faBriefcase,
  faGraduationCap,
  faSliders,
  faCalendar,
} from '@fortawesome/free-solid-svg-icons';
import type { Candidate } from '@components/ui/dashboard/ApplicantReview/types/candidate';
import { useLanguage } from '../../../../contexts/LanguageContext';
import translations from './CandidateInfoTab.translations.json';

type SupportedLanguage = keyof typeof translations;

const SectionHeader: React.FC<{ icon: any; title: string }> = ({ icon, title }) => (
  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
    <FontAwesomeIcon icon={icon} style={{ color: '#0a66c2', fontSize: 14 }} />
    <Typography variant="caption" fontWeight="bold" color="#0a66c2" sx={{ letterSpacing: 1 }}>
      {title}
    </Typography>
  </Box>
);

export const CandidateInfoTab: React.FC<{ candidate: Candidate }> = ({ candidate }) => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  return (
    <Stack spacing={3}>
      {/* PROFILE */}
      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#121316', border: '1px solid #23262d', borderRadius: 2 }}>
        <SectionHeader icon={faUser} title={content.profile} />
        <Stack direction="row" spacing={2}>
          <Box flex={1} sx={{ p: 2, backgroundColor: '#0B0C0E', borderRadius: 1.5, border: '1px solid #1F2229' }}>
            <Typography variant="caption" color="#6C727F">{content.name}</Typography>
            <Typography variant="body2" color="#FFFFFF" fontWeight={600}>{candidate.info.fullName}</Typography>
          </Box>
          <Box flex={1} sx={{ p: 2, backgroundColor: '#0B0C0E', borderRadius: 1.5, border: '1px solid #1F2229' }}>
            <Typography variant="caption" color="#6C727F" display="block">
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 6 }} /> {content.email}
            </Typography>
            <Typography variant="body2" color="#FFFFFF" fontWeight={600}>{candidate.info.email}</Typography>
          </Box>
          <Box flex={1} sx={{ p: 2, backgroundColor: '#0B0C0E', borderRadius: 1.5, border: '1px solid #1F2229' }}>
            <Typography variant="caption" color="#6C727F" display="block">
              <FontAwesomeIcon icon={faPhone} style={{ marginRight: 6 }} /> {content.phone}
            </Typography>
            <Typography variant="body2" color="#FFFFFF" fontWeight={600}>{candidate.info.phone}</Typography>
          </Box>
        </Stack>
      </Paper>

      {/* COVER LETTER */}
      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#121316', border: '1px solid #23262d', borderRadius: 2 }}>
        <SectionHeader icon={faFileLines} title={content.cover} />
        <Typography variant="body2" color="#A0A7B1" sx={{ lineHeight: 1.6 }}>
          {candidate.coverLetter}
        </Typography>
      </Paper>

      {/* PROFESSIONAL EXPERIENCE */}
      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#121316', border: '1px solid #23262d', borderRadius: 2 }}>
        <SectionHeader icon={faBriefcase} title={content.experience} />
        <Stack spacing={2}>
          {candidate.experiences.map((exp, idx) => (
            <Box key={idx} sx={{ p: 2, backgroundColor: '#0B0C0E', borderRadius: 1.5, border: '1px solid #1F2229' }}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="subtitle2" fontWeight="bold" color="#FFFFFF">{exp.company}</Typography>
                <Typography variant="caption" color="#6C727F">
                  <FontAwesomeIcon icon={faCalendar} style={{ marginRight: 6 }} /> {exp.period}
                </Typography>
              </Box>
              <Typography variant="body2" color="#A0A7B1">{exp.role}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* EDUCATION */}
      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#121316', border: '1px solid #23262d', borderRadius: 2 }}>
        <SectionHeader icon={faGraduationCap} title={content.education} />
        <Stack spacing={2}>
          {candidate.education.map((edu, idx) => (
            <Box key={idx} sx={{ p: 2, backgroundColor: '#0B0C0E', borderRadius: 1.5, border: '1px solid #1F2229' }}>
              <Box display="flex" justifyContent="space-between" mb={0.5}>
                <Typography variant="subtitle2" fontWeight="bold" color="#FFFFFF">{edu.school}</Typography>
                <Typography variant="caption" color="#6C727F">
                  <FontAwesomeIcon icon={faCalendar} style={{ marginRight: 6 }} /> {edu.period}
                </Typography>
              </Box>
              <Typography variant="body2" color="#A0A7B1">{edu.degree}</Typography>
            </Box>
          ))}
        </Stack>
      </Paper>

      {/* SKILLS */}
      <Paper elevation={0} sx={{ p: 3, backgroundColor: '#121316', border: '1px solid #23262d', borderRadius: 2 }}>
        <SectionHeader icon={faSliders} title={content.skills} />
        <Box display="flex" gap={1} flexWrap="wrap">
          {candidate.skills.map((skill, idx) => (
            <Chip
              key={idx}
              label={skill}
              sx={{
                backgroundColor: 'transparent',
                color: '#fff',
                border: '1px solid #0a66c2',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          ))}
        </Box>
      </Paper>
    </Stack>
  );
};